'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, UserPlus, Loader2, CheckCircle2 } from 'lucide-react';

type SignupFields = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
};

export default function SignupForm({ onSuccess }: { onSuccess: () => void }) {
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFields>();

  const password = watch('password', '');

  // Backend integration point: POST /api/auth/register
  const onSubmit = async (_data: SignupFields) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);
    setDone(true);
    setTimeout(onSuccess, 1800);
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-4 py-10 animate-bounce-in">
        <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
          <CheckCircle2 size={32} className="text-success" />
        </div>
        <h3 className="text-xl font-bold text-foreground">Account created!</h3>
        <p className="text-sm text-muted-foreground text-center">
          Redirecting you to sign in…
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-1">Create your account</h2>
        <p className="text-sm text-muted-foreground">Start earning free crypto in minutes</p>
      </div>

      {/* Full Name */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="signup-name" className="text-sm font-semibold text-foreground">
          Full name
        </label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          placeholder="Marcus Reyes"
          className="input-field"
          {...register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
        />
        {errors.fullName && <p className="text-xs text-danger">{errors.fullName.message}</p>}
      </div>

      {/* Email */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="signup-email" className="text-sm font-semibold text-foreground">
          Email address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          className="input-field"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-xs text-danger">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="signup-password" className="text-sm font-semibold text-foreground">
          Password
        </label>
        <p className="text-xs text-muted-foreground">Minimum 8 characters with at least one number</p>
        <div className="relative">
          <input
            id="signup-password"
            type={showPass ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            className="input-field pr-11"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Minimum 8 characters' },
              pattern: { value: /(?=.*\d)/, message: 'Must contain at least one number' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPass((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPass ? 'Hide password' : 'Show password'}
          >
            {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-danger">{errors.password.message}</p>}
      </div>

      {/* Confirm Password */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="signup-confirm" className="text-sm font-semibold text-foreground">
          Confirm password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="••••••••"
            className="input-field pr-11"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (v) => v === password || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
          >
            {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs text-danger">{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-2">
        <input
          id="agree-terms"
          type="checkbox"
          className="w-4 h-4 mt-0.5 rounded border-input accent-primary"
          {...register('agreeTerms', { required: 'You must agree to the terms' })}
        />
        <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer select-none leading-snug">
          I agree to the{' '}
          <span className="text-primary font-semibold hover:underline cursor-pointer">Terms of Service</span>
          {' '}and{' '}
          <span className="text-primary font-semibold hover:underline cursor-pointer">Privacy Policy</span>
        </label>
      </div>
      {errors.agreeTerms && <p className="text-xs text-danger -mt-2">{errors.agreeTerms.message}</p>}

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary flex items-center justify-center gap-2 w-full py-3 text-base mt-1"
        style={{ minHeight: '48px' }}
      >
        {loading ? (
          <>
            <Loader2 size={17} className="animate-spin" />
            Creating account…
          </>
        ) : (
          <>
            <UserPlus size={17} />
            Create Account — It&apos;s Free
          </>
        )}
      </button>
    </form>
  );
}